import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { CountUpPhase } from '../hooks/useLoopingCountUp';
import { BrandLink } from './ui/BrandButton';

type LandingImpactCardProps = {
  className?: string;
  emphasizeAsComplete?: boolean;
  icon: LucideIcon;
  label: string;
  note: string;
  phase: CountUpPhase;
  tone: 'analyses' | 'hours' | 'reliability';
  value: string;
  valueTestId: string;
  detail?: ReactNode;
  href?: string;
  linkLabel?: string;
};

const toneClassNames = {
  analyses: 'bg-[rgb(var(--impact-card-analyses))]',
  hours: 'bg-[rgb(var(--impact-card-hours))]',
  reliability: 'bg-[rgb(var(--impact-card-reliability))]',
};

const reliabilityClassNames = {
  detail: 'text-[rgb(var(--theme-muted))]',
  icon: 'text-[rgb(var(--theme-muted))]',
  label: 'text-[rgb(var(--impact-card-reliability-accent))]',
  note: 'text-[rgb(var(--theme-muted))]',
  value: 'text-[rgb(var(--impact-card-reliability-accent))]',
};

function CrossfadingImpactValue({ animate, value }: { animate: boolean; value: string }) {
  const currentValueRef = useRef(value);
  const [currentValue, setCurrentValue] = useState(value);
  const [previousValue, setPreviousValue] = useState<string | null>(null);

  useEffect(() => {
    if (value === currentValueRef.current) return undefined;

    const previous = currentValueRef.current;
    currentValueRef.current = value;
    setCurrentValue(value);

    if (!animate) {
      setPreviousValue(null);
      return undefined;
    }

    setPreviousValue(previous);
    const cleanupTimer = window.setTimeout(() => setPreviousValue(null), 900);
    return () => window.clearTimeout(cleanupTimer);
  }, [animate, value]);

  return (
    <span className="impact-value-crossfade">
      {previousValue !== null && (
        <span aria-hidden="true" className="impact-value-crossfade-out">
          {previousValue}
        </span>
      )}
      <span
        key={currentValue}
        data-current-impact-value="true"
        className={previousValue !== null ? 'impact-value-crossfade-in' : undefined}
      >
        {currentValue}
      </span>
    </span>
  );
}

export function LandingImpactCard({
  className = '',
  detail,
  emphasizeAsComplete = false,
  icon: Icon,
  href,
  label,
  linkLabel,
  note,
  phase,
  tone,
  value,
  valueTestId,
}: LandingImpactCardProps) {
  const isReliability = tone === 'reliability';
  const desktopCardClassName = isReliability ? 'lg:p-12' : 'lg:p-6';
  const desktopContentClassName = isReliability ? 'lg:min-h-[26rem]' : 'lg:min-h-0';
  const desktopDetailClassName = isReliability
    ? 'lg:mt-3 lg:min-h-14 lg:text-lg'
    : 'lg:mt-2 lg:min-h-6 lg:text-sm';
  const desktopIconClassName = isReliability
    ? 'lg:right-12 lg:top-12 lg:h-28 lg:w-28'
    : 'lg:right-6 lg:top-6 lg:h-20 lg:w-20';
  const desktopLabelClassName = isReliability
    ? 'lg:mt-5 lg:min-h-14 lg:text-xl'
    : 'lg:mt-3 lg:min-h-0 lg:text-base';
  const desktopNoteClassName = isReliability
    ? 'lg:mt-8 lg:text-sm'
    : 'lg:mt-3 lg:text-xs';
  const mobileDetailVisibilityClassName = detail == null ? 'hidden sm:block' : '';
  const mobileContentPositionClassName = isReliability ? 'mt-auto' : 'mt-10';

  return (
    <article
      data-testid={`impact-${tone}-card`}
      className={`${toneClassNames[tone]} ${className} ${desktopCardClassName} relative flex h-full min-h-44 flex-col overflow-hidden rounded-[1.25rem] p-3 text-white sm:min-h-[28rem] sm:rounded-[2rem] sm:p-6 lg:min-h-0`}
    >
      <Icon
        aria-hidden="true"
        className={`absolute right-3 top-3 h-10 w-10 stroke-[1.15] sm:right-6 sm:top-6 sm:h-20 sm:w-20 ${desktopIconClassName} ${isReliability ? reliabilityClassNames.icon : 'text-white/85'}`}
      />
      <div className={`${mobileContentPositionClassName} flex min-h-0 flex-col sm:mt-auto sm:min-h-[25rem] ${desktopContentClassName}`}>
        <p
          data-phase={emphasizeAsComplete ? 'shining' : phase}
          data-testid={valueTestId}
          className={`impact-score whitespace-nowrap text-[clamp(1.5rem,7vw,6.5rem)] font-bold leading-none tracking-tight tabular-nums sm:text-[clamp(2.25rem,5vw,6.5rem)] ${isReliability ? reliabilityClassNames.value : 'text-white'}`}
        >
          <CrossfadingImpactValue animate={phase === 'counting'} value={value} />
        </p>
        <h3
          data-phase={phase}
          className={`impact-score-meaning mt-2 min-h-10 text-[11px] font-semibold uppercase leading-[1.35] tracking-wide sm:mt-5 sm:min-h-14 sm:text-xl ${desktopLabelClassName} ${isReliability ? reliabilityClassNames.label : 'text-white'}`}
        >
          {label}
        </h3>
        <div className={`${mobileDetailVisibilityClassName} mt-1 min-h-10 text-xs font-semibold leading-[1.35] sm:mt-3 sm:min-h-14 sm:text-lg ${desktopDetailClassName} ${isReliability ? reliabilityClassNames.detail : 'text-white/90'}`}>
          {detail}
        </div>
        <p
          data-testid={`impact-${tone}-note`}
          className={`mt-2 hidden max-w-xl text-[10px] italic leading-[1.45] sm:mt-8 sm:block sm:text-sm sm:leading-relaxed ${desktopNoteClassName} ${isReliability ? reliabilityClassNames.note : 'text-white/85'}`}
        >
          {note}
        </p>
        {href && linkLabel && (
          <BrandLink
            href={href}
            size="sm"
            className="mt-3 max-w-full self-center text-center text-xs sm:mt-7 sm:text-base"
          >
            {linkLabel}
          </BrandLink>
        )}
      </div>
    </article>
  );
}
